/**
 * Element presets for the floor-plan editor: the non-table things in a hall
 * (stage, dance floor, bars, tents, doors, sections...). Each preset is just a
 * zone `type` string plus defaults, so nothing new is stored: the zones table
 * has no constraint on `type`.
 *
 * Colours are design-system token names (resolved at draw time), never hex.
 */
import { canonicalSize, centreOf, directionToward, opposite, toWorld } from './tableGeometry';
import type { Box, Direction, Point } from './tableGeometry';

export type ElementKind = 'rect' | 'ellipse' | 'stage-tiers' | 'route' | 'section' | 'divider';

export interface ElementPreset {
    type: string;
    label: string;
    kind: ElementKind;
    width: number;
    height: number;
    /** design-system CSS custom property used as the fill */
    tone: string;
    dashed?: boolean;
    /** name is "<label> <n>" and n is the next free number */
    numbered?: boolean;
}

export const ELEMENT_PRESETS: readonly ElementPreset[] = [
    { type: 'stage_tiered',  label: 'Four-tier stage',  kind: 'stage-tiers', width: 360, height: 130, tone: '--badge-info-bg' },
    { type: 'dance_round',   label: 'Dance floor',      kind: 'ellipse',     width: 170, height: 170, tone: '--purple-bg' },
    { type: 'bar',           label: 'Bar',              kind: 'rect',        width: 110, height: 36,  tone: '--warning-bg', numbered: true },
    { type: 'vip_entrance',  label: 'VIP entrance',     kind: 'route',       width: 36,  height: 170, tone: '--success-bg' },
    { type: 'activity_tent', label: 'Activity tent',    kind: 'rect',        width: 150, height: 100, tone: '--badge-teal-bg', dashed: true },
    { type: 'photo_spot',    label: 'Photo spot',       kind: 'rect',        width: 70,  height: 60,  tone: '--info-bg', numbered: true },
    { type: 'aides_tent',    label: 'Aides tent',       kind: 'rect',        width: 130, height: 90,  tone: '--neutral-bg', dashed: true },
    { type: 'souvenir_room', label: 'Souvenir room',    kind: 'rect',        width: 120, height: 80,  tone: '--primary-light' },
    { type: 'door',          label: 'Door',             kind: 'rect',        width: 14,  height: 40,  tone: '--text-muted', numbered: true },
    { type: 'platform',      label: 'Low platform',     kind: 'rect',        width: 140, height: 50,  tone: '--bg-soft', numbered: true },
    { type: 'divider',       label: 'Plant / rope divider', kind: 'divider', width: 200, height: 12,  tone: '--success', dashed: true },
    { type: 'section',       label: 'Section',          kind: 'section',     width: 400, height: 300, tone: '--border-medium', dashed: true },
];

const BY_TYPE = new Map(ELEMENT_PRESETS.map(p => [p.type, p]));

export function presetFor(type: string): ElementPreset | undefined {
    return BY_TYPE.get(type);
}

export function isElementType(type: string): boolean {
    return BY_TYPE.has(type);
}

/** Default name for a new element: "Door 3", "Bar 2", or just the label. */
export function defaultElementName(preset: ElementPreset, rooms: ReadonlyArray<{ type: string; name: string }>): string {
    if (preset.type === 'section') {
        const used = new Set(rooms.filter(r => r.type === 'section').map(r => r.name.trim().toUpperCase()));
        return ['A', 'B', 'C', 'D'].find(l => !used.has(l)) ?? 'A';
    }
    if (!preset.numbered) return preset.label;
    let max = 0;
    for (const r of rooms) {
        if (r.type !== preset.type) continue;
        const m = /(\d+)\s*$/.exec(r.name);
        if (m && Number(m[1]) > max) max = Number(m[1]);
    }
    return `${preset.label} ${max + 1}`;
}

// ── Four-tier semicircular stage ─────────────────────────────────────────────
/**
 * Nested half-ellipses sharing one flat edge (the wall side), biggest first.
 * `bulge` is the way the curved side points (toward the audience).
 */
export function stageTierOutlines(box: Box, bulge: Direction = 'down', tiers = 4, steps = 24): Point[][] {
    const facing = opposite(bulge);
    const { w, h } = canonicalSize(box, facing);
    const out: Point[][] = [];
    for (let k = 0; k < tiers; k++) {
        const s = 1 - k * 0.22;
        const rx = (w / 2) * s;
        const ry = h * s;
        const pts: Point[] = [];
        for (let i = 0; i <= steps; i++) {
            const phi = (Math.PI * i) / steps;
            pts.push(toWorld(box, facing, rx * Math.cos(phi), -h / 2 + ry * Math.sin(phi)));
        }
        out.push(pts);
    }
    return out;
}

/** Direction the stage bulges: toward the audience (centroid of the tables), default down. */
export function stageBulge(box: Box, audience: Point | null): Direction {
    return directionToward(box, audience, 'down');
}

// ── VIP entrance route line ──────────────────────────────────────────────────
export interface RouteLine { from: Point; to: Point; head: [Point, Point] }

/**
 * A line along the element's long axis, pointing toward `target` (the hall).
 * Ends are inset so the line sits inside the box.
 */
export function routeLine(box: Box, target: Point | null): RouteLine {
    const c = centreOf(box);
    const vertical = box.height > box.width;
    const inset = 10;
    const half = (vertical ? box.height : box.width) / 2 - inset;
    // along the long axis; with no target the arrow points down / right
    const sign = !target || (vertical ? target.y >= c.y : target.x >= c.x) ? 1 : -1;
    const from: Point = vertical ? { x: c.x, y: c.y - sign * half } : { x: c.x - sign * half, y: c.y };
    const to: Point = vertical ? { x: c.x, y: c.y + sign * half } : { x: c.x + sign * half, y: c.y };
    const barb = 8;
    const head: [Point, Point] = vertical
        ? [{ x: to.x - barb, y: to.y - sign * barb }, { x: to.x + barb, y: to.y - sign * barb }]
        : [{ x: to.x - sign * barb, y: to.y - barb }, { x: to.x - sign * barb, y: to.y + barb }];
    return { from, to, head };
}
